import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  RosterSettings,
  ScholarshipModel,
  ScholarshipPlayer,
} from '../../../../types/scholarships';
import { tourAnchor } from '../../../tours/tourAnchors';
import { DragHandleIcon } from './icons';
import { EMPTY_ROSTER_SETTINGS, indicatorNames, resolveTrait } from './rosterPalette';
import { HandednessName, IndicatorDots, TraitPill } from './RosterPlayerMarks';
import {
  depthChartValueIsMoney,
  formatPlayerValue,
  totalPlayerMoney,
} from './scholarshipPercent';
import { formatMoney } from './scholarshipMoney';
import { compactClassLabel } from './scholarshipClasses';
import { rowActionLabel } from './openableProspect';

/* Academic aid is added into the row's figure where that figure is DOLLARS — coaches asked to see
   what a player is getting in one number, so a player on $50k athletic and $25k academic reads as
   $75,000. It is not added into a percent, because a percent is an equivalency against cost and
   academic aid is not part of that.
                                                                                              
   The chip stays either way. It is what tells a coach how the total is made up, and which half of
   it the athletic budget actually pays for — the tooltips below say so outright. */
const ACADEMIC_TITLE_PRIMARY = 'Academic aid — does not count toward the athletic budget';
const ACADEMIC_TITLE_SECONDARY =
  "Academic aid — does not count toward the athletic budget, and is recorded on this player's primary position";

interface Props {
  player: ScholarshipPlayer;
  depthIndex: number;
  appearance: 'primary' | 'secondary';
  scholarshipModel: ScholarshipModel;
  /** The board's settings, for the name colour, trait pill and dots. Omitted: none of the three. */
  settings?: RosterSettings;
  /** Primary rows on a card with more than one: this row has a handle and sorts. */
  draggable?: boolean;
  /** The card has handles, so a row without one keeps the space and the columns line up. */
  showHandleSpace?: boolean;
  /** Carries the `rosters.depth.drag` tour anchor (first handle on the chart). */
  dragAnchor?: boolean;
  onClick: () => void;
}

export const DepthChartCardRow = ({
  player,
  depthIndex,
  appearance,
  scholarshipModel,
  settings = EMPTY_ROSTER_SETTINGS,
  draggable = false,
  showHandleSpace = false,
  dragAnchor = false,
  onClick,
}: Props) => {
  const sortable = useSortable({ id: player.id, disabled: !draggable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const dragStyle: React.CSSProperties | undefined = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        position: isDragging ? 'relative' : undefined,
        zIndex: isDragging ? 1 : undefined,
      }
    : undefined;
  const trait = resolveTrait(player.evaluationTraitId, settings);
  const indicators = indicatorNames(player.indicatorIds, settings);
  const percent = formatPlayerValue(player, scholarshipModel);
  const playerLabel = `${player.firstName} ${player.lastName}`;
  const isSecondary = appearance === 'secondary';
  /* An attached player's class comes from their player record, so reading player.class alone
     showed a dash on the card for every player who came off the prospect list. */
  const classLabel = compactClassLabel(player);
  /* Rendered only when there is money to show: null (never recorded) and 0 (recorded as
     nothing) both read as "no academic aid", and a dense card should not carry either. */
  const academicAmount = player.academicAmount ?? 0;
  const academicText = academicAmount > 0 ? formatMoney(academicAmount) : null;
  /* Only where the figure is a combined dollar total AND there is academic aid in it. Anywhere
     else there is nothing to explain: a percent has no academic money folded into it, and a total
     with no academic aid is just the athletic money. */
  const showsCombinedMoney =
    academicAmount > 0 && depthChartValueIsMoney(player, scholarshipModel);
  const combinedTitle = showsCombinedMoney
    ? `Total money: ${formatMoney(totalPlayerMoney(player))} athletic + ${formatMoney(
        academicAmount,
      )} academic. Only the athletic half counts toward the budget.`
    : undefined;
  const academicSuffix = academicText
    ? `, academic aid ${academicText}${isSecondary ? ' on their primary position' : ''}`
    : '';

  return (
    <li
      ref={draggable ? setNodeRef : undefined}
      style={dragStyle}
      className={`depth-chart-card__row${isSecondary ? ' depth-chart-card__row--secondary' : ''}`}
    >
      {draggable ? (
        <button
          type="button"
          className="roster-drag-handle depth-chart-card__drag-handle"
          aria-label={`Drag to reorder ${playerLabel}`}
          {...(dragAnchor ? tourAnchor('rosters.depth.drag') : {})}
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon />
        </button>
      ) : (
        showHandleSpace && <span className="depth-chart-card__drag-spacer" aria-hidden="true" />
      )}
      <button
        type="button"
        className="depth-chart-card__row-body"
        onClick={onClick}
        /* The label replaces the row's inner text for a screen reader, so the class has to be
           said here or it is announced nowhere. */
        aria-label={`${rowActionLabel(player, playerLabel)}, ${classLabel ? `class ${classLabel}, ` : ''}rank ${
          depthIndex + 1
        }${isSecondary ? ', secondary position here' : ''}${trait ? `, ${trait.name}` : ''}${
          indicators ? `, ${indicators}` : ''
        }${academicSuffix}`}
      >
        <span className="depth-chart-card__lead">
          {/* Only a borrowed (secondary) row is marked. A star on every primary row cost each
              name ~12px on a card that has none to spare, and said nothing the rank did not. */}
          {isSecondary && (
            <span
              className="depth-chart-card__marker depth-chart-card__marker--secondary"
              aria-hidden="true"
              title="Secondary position"
            >
              ○
            </span>
          )}
          {/* Secondaries are bucketed after primaries, so the rank keeps counting
              down the card (1, 2 primary → 3, 4 secondary) rather than restarting. */}
          <span
            className={`depth-chart-card__rank${isSecondary ? ' depth-chart-card__rank--secondary' : ''}`}
          >
            {depthIndex + 1}.
          </span>
        </span>
        <span className="depth-chart-card__name-block">
          <IndicatorDots indicatorIds={player.indicatorIds} settings={settings} announce={false} />
          <HandednessName
            name={playerLabel}
            bats={player.bats}
            settings={settings}
            className="depth-chart-card__name"
          />
        </span>
        {/* The second line: everything that describes the player rather than names them. A card
            is ~200px wide on a laptop, so one line for name, class, B/T, pill and money left
            the name a few pixels wide and every cell drawn over its neighbour. */}
        <span className="depth-chart-card__sub">
          <span className="depth-chart-card__meta">
            {classLabel ?? '—'} · {player.bats ?? '—'}/{player.throws ?? '—'}
          </span>
          <TraitPill traitId={player.evaluationTraitId} settings={settings} />
          {player.isDsa && (
            <span
              className="depth-chart-card__dsa-badge"
              title="Designated Student Athlete — excluded from the roster limit"
            >
              DSA
            </span>
          )}
          {/* Its own chip, so it never reads as part of the athletic figure. Secondaries keep it
              quieted-but-legible for the same reason the athletic figure stays visible:
              blanking it would say "no academic aid" rather than "counted on the primary
              position". */}
          {academicText && (
            <span
              className={`depth-chart-card__academic${isSecondary ? ' depth-chart-card__academic--secondary' : ''}`}
              title={isSecondary ? ACADEMIC_TITLE_SECONDARY : ACADEMIC_TITLE_PRIMARY}
            >
              <span className="depth-chart-card__academic-tag">ACAD</span>
              <span className="depth-chart-card__academic-value">{academicText}</span>
            </span>
          )}
          {/* Secondaries show the same figure as primaries. It used to be a bare dash, which
              read as "this player has no scholarship" rather than the intended "it is counted
              elsewhere" — so a coach scanning the card had to go find the player's primary
              position to learn what they were on. The dimmed styling plus the tooltip carry
              that caveat now; the card's count and average still use primaries only, so
              nothing here is double-counted. */}
          <span
            className={[
              'depth-chart-card__percent',
              isSecondary ? 'depth-chart-card__percent--secondary' : '',
              percent.isWalkOn ? 'depth-chart-card__percent--walkon' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            title={
              isSecondary
                ? "Scholarship counts on this player's primary position, not toward this one"
                : percent.isWalkOn
                  ? 'Walk-on — no scholarship awarded'
                  : combinedTitle
            }
          >
            {percent.text}
          </span>
        </span>
      </button>
    </li>
  );
};
